import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import GoldShieldLogo from "@/components/GoldShieldLogo";

const TermsAndConditions = () => (
  <div className="min-h-screen bg-slate-950 text-white flex flex-col">
    <SEO 
      title="Terms and Conditions | RTS EXPRESS LTD"
      description="Terms and conditions for RTS EXPRESS LTD same-day courier services. Read our service agreement, booking terms and liability information."
      path="/terms-and-conditions"
    />
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="legal-nav-home-link">
          <GoldShieldLogo className="w-10 h-12" />
          <div className="flex flex-col">
            <span className="text-xl font-bold text-white tracking-tight leading-tight">RTS EXPRESS</span>
            <span className="text-[10px] text-slate-400 tracking-wide">SAME DAY COURIER</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="/#services" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-services-link">Services</a>
          <a href="/#how-it-works" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-how-link">How It Works</a>
          <a href="/#contact" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-contact-link">Contact</a>
        </div>
        <Link to="/get-quote" className="bg-green-500 text-slate-950 hover:bg-green-500/90 font-semibold px-6 py-2 rounded-sm transition-colors" data-testid="legal-get-quote-btn">
          Get a Quote
        </Link>
      </div>
    </nav>

    <main className="flex-1 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="legal-page-title">Terms &amp; Conditions</h1>
        <div className="text-slate-300 space-y-4" data-testid="legal-page-content">
          <p><strong>Last updated: 25 February 2026</strong></p>

          <h2 className="text-2xl font-semibold text-white">1. About us</h2>
          <p>
            These Terms &amp; Conditions (“Terms”) apply to your use of the RTS EXPRESS LTD website and to requests for quotations and courier services arranged through it.
          </p>
          <p>
            Company: RTS EXPRESS LTD (Company No. 13973121)
            <br />
            Registered office / headquarters: 1 Bradestone Road, Nuneaton, Warwickshire, CV11 4QR, United Kingdom
            <br />
            Contact: info@rtsexpress.co.uk
          </p>

          <h2 className="text-2xl font-semibold text-white">2. Our service model (important)</h2>
          <p>
            RTS EXPRESS LTD provides premium, dedicated, time-critical courier services within the United Kingdom mainland (England, Scotland, Wales). We operate from Nuneaton. We do not provide multi-drop courier services.
          </p>
          <p>
            A “Quote Request” submitted via the website is not an order and does not create a binding contract.
          </p>
          <p>A booking is confirmed only when:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>we issue a written quotation, and</li>
            <li>you accept the quotation by completing payment via our payment link (Stripe) and we confirm receipt of payment.</li>
          </ul>
          <p>
            A legally binding contract between RTS EXPRESS LTD and the customer is formed only upon confirmation of successful payment.
          </p>
          <p>
            If we do not issue a quotation, or you do not pay, no booking exists and we have no obligation to provide services.
          </p>

          <h2 className="text-2xl font-semibold text-white">3. Eligibility: Business customers only</h2>
          <p>
            RTS EXPRESS LTD provides services exclusively to business customers.
          </p>
          <p>
            By requesting a quotation or making a booking, you confirm that you are acting in the course of a business, trade, profession, or commercial activity.
          </p>
          <p>
            We do not accept bookings from individual consumers acting for personal purposes.
          </p>
          <p>
            Accordingly, the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 and other consumer protection legislation do not apply to our services.
          </p>

          <h2 className="text-2xl font-semibold text-white">4. Quote requests: accuracy of information</h2>
          <p>
            You must provide accurate and complete information including collection/delivery addresses, contact details, dates/times, item description, weight, dimensions, and any access restrictions.
          </p>
          <p>If information is incorrect or incomplete, we may:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>amend the quotation,</li>
            <li>apply additional charges, and/or</li>
            <li>refuse collection.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white">5. What we carry (and what we do not)</h2>
          <p>
            We may carry documents, parcels and freight suitable for transport by a 3.5t van, including up to 2 pallets per trip subject to legal limits and vehicle capacity.
          </p>
          <p>We do not carry (unless expressly agreed in writing in advance):</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>hazardous, dangerous or prohibited goods,</li>
            <li>illegal items,</li>
            <li>explosives, firearms, ammunition,</li>
            <li>controlled drugs,</li>
            <li>perishable goods requiring temperature control,</li>
            <li>fragile items unless declared and appropriately packed,</li>
            <li>cash, jewellery, precious metals/stones,</li>
            <li>high-value goods above the agreed limit (see Liability section),</li>
            <li>any item requiring special licences/handling we have not agreed to provide.</li>
          </ul>
          <p>
            We may refuse goods that appear unsafe, inadequately packaged, or misdeclared.
          </p>

          <h2 className="text-2xl font-semibold text-white">6. Vehicle Capacity and Loading Responsibilities</h2>
          <p className="font-semibold text-white">Vehicle Capacity and Operational Weight Limit</p>
          <p>
            RTS EXPRESS LTD operates vehicles with a maximum gross vehicle weight of 3.5 tonnes.
          </p>
          <p>
            For operational safety, axle compliance, and regulatory adherence, the maximum total payload accepted per vehicle is strictly limited to 1050 kilograms.
          </p>
          <p>
            This limit applies to the total combined weight of all goods loaded onto the vehicle.
          </p>
          <p>The Company reserves the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Refuse loading where the declared or actual weight exceeds 1050 kg;</li>
            <li>Cancel the service if weight has been misdeclared;</li>
            <li>Apply additional charges if incorrect weight information affects operational planning.</li>
          </ul>
          <p>
            Compliance with UK road traffic and vehicle weight regulations takes precedence over all booking confirmations.
          </p>
          <p className="font-semibold text-white mt-6">Loading and Unloading Responsibilities</p>
          <p>
            RTS EXPRESS LTD provides a dedicated transport service.
          </p>
          <p>
            Driver responsibilities are limited to vehicle positioning and safe securing of goods within the vehicle.
          </p>
          <p>
            Manual handling by the driver is permitted only for small parcels, boxes, or documents that can be safely lifted by one individual without mechanical assistance.
          </p>
          <p>For palletised freight, machinery, engines, industrial components, or any goods requiring mechanical handling equipment:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>The sender is fully responsible for safe mechanical loading;</li>
            <li>The recipient is fully responsible for safe mechanical unloading;</li>
            <li>Appropriate equipment (e.g., forklift, loading bay access) must be available at collection and delivery points.</li>
          </ul>
          <p>
            The Company does not provide tail lift equipment, forklifts, pallet trucks, or mechanical lifting devices.
          </p>
          <p>If suitable loading or unloading facilities are not available, the driver may:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Refuse to load or unload the goods;</li>
            <li>Suspend the delivery;</li>
            <li>Apply reasonable waiting time charges.</li>
          </ul>
          <p>
            Responsibility for safe loading and unloading of palletised or mechanically handled goods remains with the sender and/or recipient.
          </p>
          <p>
            Nothing in this clause excludes liability for negligence directly caused by the Company.
          </p>

          <h2 className="text-2xl font-semibold text-white">7. Collection and delivery times</h2>
          <p>
            Any collection/delivery times provided by you are requested times and may not be achievable depending on distance from Nuneaton and operational constraints. We will confirm feasible timings when we issue the quotation.
          </p>
          <p>You are responsible for ensuring:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>goods are ready for collection at the agreed time,</li>
            <li>safe/legal access exists,</li>
            <li>appropriate loading/unloading facilities are available.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white">8. Waiting time (delays at collection or delivery)</h2>
          <p>
            Our quotation assumes goods are ready and access is available at the agreed time.
          </p>
          <p>
            Free waiting time: first 15 minutes at collection and/or delivery.
            <br />
            After that, we reserve the right to charge £1 per minute (or part minute), plus any additional costs incurred.
          </p>

          <h2 className="text-2xl font-semibold text-white">9. Surcharges and pass-through charges</h2>
          <p>You are responsible for charges incurred in performing the service, including (where applicable):</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Congestion Charge,</li>
            <li>ULEZ,</li>
            <li>tolls,</li>
            <li>parking fees,</li>
            <li>access permits,</li>
            <li>any other road-user charges.</li>
          </ul>
          <p>
            If such charges are not known at the time of quotation, we may invoice them afterwards or include them in an updated quotation.
          </p>

          <h2 className="text-2xl font-semibold text-white">10. Failed collection / redelivery / incorrect declaration</h2>
          <p>We may charge additional fees where:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Failed collection: goods are not available, access is refused, contact is unavailable, or the details provided are wrong;</li>
            <li>Redelivery/return: delivery cannot be completed due to recipient unavailability or incorrect address;</li>
            <li>Incorrect weight/dimensions: actual weight/dimensions differ materially from declared values;</li>
            <li>Unsafe loading/unloading: site conditions are unsafe or illegal for loading/unloading.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white">11. Payment, invoices and VAT</h2>
          <p>
            Payment is required via our payment link (Stripe) unless we expressly agree otherwise in writing.
          </p>
          <p>
            Where VAT applies, it will be shown on your invoice/receipt.
          </p>

          <h2 className="text-2xl font-semibold text-white">12. Cancellations and refunds (premium model)</h2>
          <p>
            Because our service is time-critical and capacity is reserved once a booking is confirmed:
          </p>
          <p>
            Customer cancellation after payment: payments are non-refundable, except where a refund is required by law.
          </p>
          <p>
            As our services are provided exclusively to business customers, consumer cancellation rights do not apply.
          </p>
          <p>
            If we cancel the booking and cannot provide an alternative acceptable to you, you will be entitled to a refund of amounts paid for the cancelled service (excluding completed portions, pass-through charges already incurred, or fees permitted by law).
          </p>

          <h2 className="text-2xl font-semibold text-white">13. Liability and claims (important)</h2>
          <p>Our liability is limited as follows:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Our liability shall in no event exceed the lower of: the declared value of the goods, or £10,000 per consignment.</li>
            <li>High-value goods: goods with a value above £5,000 must be declared in advance and accepted by us in writing. If not declared/accepted, our liability may be limited to £5,000 or less.</li>
          </ul>
          <p>We are not liable for:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>losses caused by your incorrect/incomplete information,</li>
            <li>inadequate packaging,</li>
            <li>inherent vice/fragility of goods,</li>
            <li>loss of profit,</li>
            <li>loss of production,</li>
            <li>business interruption,</li>
            <li>indirect or consequential losses,</li>
            <li>delays caused by events outside our reasonable control (traffic incidents, severe weather, road closures, strikes, etc.).</li>
          </ul>
          <p>
            Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or any liability that cannot be excluded under UK law.
          </p>

          <h2 className="text-2xl font-semibold text-white">14. Non-Inspection of Goods</h2>
          <p>
            RTS EXPRESS LTD provides dedicated transport services and does not inspect, open, count, test, or verify the internal contents of sealed packages, pallets, cartons, or containers.
          </p>
          <p>
            We transport goods strictly as presented by the sender.
          </p>
          <p>Unless expressly agreed in writing prior to collection, we do not verify:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>internal quantity;</li>
            <li>functionality;</li>
            <li>model numbers;</li>
            <li>serial numbers;</li>
            <li>condition inside sealed packaging.</li>
          </ul>
          <p>
            The sender is solely responsible for ensuring accuracy of declared contents.
          </p>

          <h2 className="text-2xl font-semibold text-white">15. Packaging Responsibility</h2>
          <p>
            The sender is solely responsible for ensuring that all goods are adequately packaged and secured for road transportation.
          </p>
          <p>RTS EXPRESS LTD shall not be liable for any loss or damage arising from:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>inadequate packaging;</li>
            <li>insufficient internal protection;</li>
            <li>inappropriate palletisation;</li>
            <li>unsecured or unstable loading prepared by the sender.</li>
          </ul>
          <p>
            All goods must be packaged to withstand normal conditions of road transport.
          </p>

          <h2 className="text-2xl font-semibold text-white">16. Proof of Delivery and Transfer of Responsibility</h2>
          <p>Delivery is deemed completed when:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>the goods are delivered to the delivery address provided by the customer; and</li>
            <li>a signed confirmation including printed name, signature, and time of delivery is obtained.</li>
          </ul>
          <p>
            By signing the delivery documentation, the recipient confirms receipt of the goods in apparent good condition.
          </p>
          <p>
            Upon signed confirmation, responsibility for the goods transfers immediately to the recipient.
          </p>
          <p>
            Any claims relating to visible damage must be noted at the time of delivery.
          </p>

          <h2 className="text-2xl font-semibold text-white">17. Notification of Claims</h2>
          <p>Any claim for loss or damage must be notified to RTS EXPRESS LTD in writing:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>within 24 hours of delivery for visible damage;</li>
            <li>within 48 hours for concealed damage;</li>
            <li>within 7 calendar days for loss claims.</li>
          </ul>
          <p>
            Failure to notify within these timeframes may invalidate the claim.
          </p>

          <h2 className="text-2xl font-semibold text-white">18. Goods in Transit and insurance</h2>
          <p>
            We maintain Goods in Transit cover; however, insurance does not increase our liability beyond the limits in these Terms unless agreed in writing.
          </p>

          <h2 className="text-2xl font-semibold text-white">19. Subcontractors</h2>
          <p>
            We may use vetted subcontractors/owner-drivers to perform services. Where we do, these Terms still apply and we remain your contracting party.
          </p>

          <h2 className="text-2xl font-semibold text-white">20. No international / no ferry routes / no Northern Ireland</h2>
          <p>
            We provide services on UK mainland (England, Scotland, Wales). We do not provide services requiring ferry crossings or customs-controlled routes, and we do not transport goods to/from Northern Ireland or outside the United Kingdom.
          </p>

          <h2 className="text-2xl font-semibold text-white">21. Data protection</h2>
          <p>
            Our handling of personal data is described in our Privacy Policy. RTS EXPRESS LTD is registered with the UK Information Commissioner's Office (ICO), registration reference: ZC095659.
          </p>

          <h2 className="text-2xl font-semibold text-white">22. Website use</h2>
          <p>
            You must not misuse the website, attempt unauthorised access, or introduce malware. We may suspend access where misuse is suspected.
          </p>

          <h2 className="text-2xl font-semibold text-white">23. Complaints</h2>
          <p>
            If you have a complaint, contact us at info@rtsexpress.co.uk and we will respond within a reasonable time.
          </p>

          <h2 className="text-2xl font-semibold text-white">24. Governing law</h2>
          <p>
            These Terms are governed by the laws of England and Wales. The courts of England and Wales will have exclusive jurisdiction.
          </p>

        <p>
          General enquiries: info@rtsexpress.co.uk
          <br />
          Billing enquiries: accounts@rtsexpress.co.uk
        </p>
        </div>
      </div>
    </main>

    <footer className="border-t border-slate-800 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm" data-testid="legal-footer-copyright">
            Copyright {new Date().getFullYear()} RTS EXPRESS LTD. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>Company No: 13973121</span>
            <span className="text-slate-700">|</span>
            <span>VAT No: GB414935787</span>
            <span className="text-slate-700">|</span>
            <span>ICO: ZC095659</span>
            <span className="text-slate-700">|</span>
            <span>Payment required before collection</span>
          </div>
        </div>
        <p className="mt-2 text-slate-500 text-sm" data-testid="legal-footer-email">
          info@rtsexpress.co.uk
        </p>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <Link to="/terms-and-conditions" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-terms-link">
            Terms &amp; Conditions
          </Link>
          <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-privacy-link">
            Privacy Policy
          </Link>
          <Link to="/cookie-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-cookie-link">
            Cookie Policy
          </Link>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="legal-footer-cookie-settings-button"
          >
            Cookie Settings
          </button>
          <Link to="/website-disclaimer" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-disclaimer-link">
            Website Disclaimer
          </Link>
        </div>
      </div>
    </footer>
  </div>
);

export default TermsAndConditions;